// driving_school_classroom_program_setup_controller.js
import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "availableToTeens",
    "currentItemwrapper",
    "dayCheckbox",
    "descriptionEnglish",
    "descriptionSpanish",
    "driveTestCourseworkThreshold",
    "driveTestMinimumCourseworkScorePercent",
    "locationTypeInPerson",
    "locationTypeRemote",
    "locationTypeOptionInPerson",
    "locationTypeOptionRemote",
    "locationTypeOptionBoth",
    "languageTypeOptionBoth",
    "languageTypeOptionEnglish",
    "languageTypeOptionSpanish",
    "gapBetweenSessions",
    'instructorIds',
    "startTime", 
    "dateSelector", 
    "timeSelector",
    "teenMinimumAge",
    "teenMinimumAgeYears",
    "teenMinimumAgeMonths",
    "repeatCustom",
    "repeatSpecificDays"
  ]

  connect() {
    this.toggleTeenMinimumAge();
    this.toggleDriveTestCourseworkThreshold();
    this.toggleRepeatMode();
  }

  toggleTeenMinimumAge() {
    if (!this.hasTeenMinimumAgeTarget || !this.hasAvailableToTeensTarget) return;

    this.teenMinimumAgeTarget.classList.toggle("u-hidden", !this.availableToTeensTarget.checked);
  }

  updateInstructorIdValues(event) {
    const wrappers = this.element.querySelectorAll('[data-driving-school-widgets-instructor-selector-target="currentItemWrapper"]');
    let instructorIds = []
    for (const wrapper of wrappers) {
      if (wrapper.dataset.instructorId != "0") {
        instructorIds.push(parseInt(wrapper.dataset.instructorId));
      }
    }
    if (this.hasInstructorIdsTarget) {
      this.instructorIdsTarget.value = JSON.stringify(instructorIds);
    }
  }

  updateStartTime() {
    if (this.dateSelectorTarget.value && this.timeSelectorTarget.value) {
      this.startTimeTarget.value = `${this.dateSelectorTarget.value}T${this.timeSelectorTarget.value}`
    }
  }

  updateSessionsPerDay(event) {
    const sessionsPerDay = Number(event.target.value);
    const showGapField = Number.isInteger(sessionsPerDay) && sessionsPerDay > 1;

    this.gapBetweenSessionsTarget.classList.toggle("u-hidden", !showGapField);
  }

  updateLanguageOption() {
    if (this.languageTypeOptionBothTarget.checked) {
      // show both descriptionEnglish and descriptionSpanish
      this.showBoth();
      return
    }

    if (this.languageTypeOptionEnglishTarget.checked) {
      this.showEnglish();
      return
    }

    if (this.languageTypeOptionSpanishTarget.checked) {
      this.showSpanish();
      return
    }
  }
  updateLocationType() {
    if (this.locationTypeOptionInPersonTarget.checked) {
      // locationTypeInPerson show this hide remote
      this.locationTypeInPersonTarget.classList.remove("u-hidden");
      this.locationTypeRemoteTarget.classList.add("u-hidden");
    }

    if (this.locationTypeOptionRemoteTarget.checked) {
      this.locationTypeInPersonTarget.classList.add("u-hidden");
      this.locationTypeRemoteTarget.classList.remove("u-hidden");
    }

    if (this.locationTypeOptionBothTarget.checked) {
      this.locationTypeInPersonTarget.classList.remove("u-hidden");
      this.locationTypeRemoteTarget.classList.remove("u-hidden");
    }
  }

  toggleDriveTestCourseworkThreshold() {
    if (!this.hasDriveTestCourseworkThresholdTarget) return;

    const shouldShow = this.driveTestSchedulingLimitedByCoursework();

    this.driveTestCourseworkThresholdTarget.classList.toggle("u-hidden", !shouldShow);

    if (this.hasDriveTestMinimumCourseworkScorePercentTarget) {
      this.driveTestMinimumCourseworkScorePercentTarget.disabled = !shouldShow;
    }
  }

  toggleRepeatMode() {
    const selectedRepeatMode = this.element.querySelector('input[name$="[repeat_mode]"]:checked');
    const shouldShowSpecificDays = selectedRepeatMode?.value === "specific_days";
    const shouldShowCustom = selectedRepeatMode?.value === "custom";

    if (this.hasRepeatSpecificDaysTarget) {
      this.repeatSpecificDaysTarget.classList.toggle("u-hidden", !shouldShowSpecificDays);
    }

    if (this.hasRepeatCustomTarget) {
      this.repeatCustomTarget.classList.toggle("u-hidden", !shouldShowCustom);
    }
  }

  updateVisibility() {
    const selectedLanguage = document.querySelector('input[name="language"]:checked').value;
    if (selectedLanguage === 'en') {
      this.showEnglish();
    } else if (selectedLanguage === 'es') {
      this.showSpanish();
    } else {
      this.showBoth();
    }
  }

  showEnglish() {
    this.descriptionEnglishTargets.forEach(target => {
      target.classList.remove("u-hidden");
    });
    this.descriptionSpanishTargets.forEach(target => {
      target.classList.add("u-hidden");
    });
  }

  showSpanish() {
    this.descriptionEnglishTargets.forEach(target => {
      target.classList.add("u-hidden");
    });
    this.descriptionSpanishTargets.forEach(target => {
      target.classList.remove("u-hidden");
    });
  }

  showBoth() {
    this.descriptionEnglishTargets.forEach(target => {
      target.classList.remove("u-hidden");
    });
    this.descriptionSpanishTargets.forEach(target => {
      target.classList.remove("u-hidden");
    });
  }

  validate(event) {
    const isEdit = event.currentTarget.dataset.edit === "true";
    const errors = this.validateForm(isEdit);
    
    if (errors.length > 0) {
      event.preventDefault();
      this.showValidationBlock(errors.join("<br>"));
      return;
    }    

    if (!isEdit && !this.confirmMultipleProgramCreation()) {
      event.preventDefault();
      return;
    }

    if (!isEdit && !this.scheduleConflictsConfirmed()) {
      event.preventDefault();
      this.checkScheduleConflicts(event.currentTarget);
    }
  }

  confirmMultipleProgramCreation() {
    const programCount = this.classroomProgramCreationCount();
    if (programCount <= 1) return true;

    return window.confirm(
      `You are about to create ${programCount} classroom programs, are you sure?`
    );
  }

  scheduleConflictsConfirmed() {
    return this.element.querySelector('input[name="confirm_schedule_conflicts"]')?.value === "1";
  }

  async checkScheduleConflicts(submitButton) {
    if (this.conflictCheckInProgress) return;

    const conflictCheckUrl = this.element.dataset.conflictCheckUrl;
    if (!conflictCheckUrl) return;

    this.conflictCheckInProgress = true;
    submitButton.disabled = true;

    try {
      const response = await fetch(conflictCheckUrl, {
        method: "POST",
        body: new FormData(this.element),
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      const result = await response.json();
      if (!response.ok) {
        this.showValidationBlock((result.errors || ["Unable to check the classroom schedule."]).join("<br>"));
        return;
      }

      if (!this.confirmScheduleConflicts(result.conflicts || [])) return;

      let confirmationInput = this.element.querySelector('input[name="confirm_schedule_conflicts"]');
      if (!confirmationInput) {
        confirmationInput = document.createElement("input");
        confirmationInput.type = "hidden";
        confirmationInput.name = "confirm_schedule_conflicts";
        this.element.appendChild(confirmationInput);
      }
      confirmationInput.value = "1";

      submitButton.disabled = false;
      if (this.element.requestSubmit) {
        this.element.requestSubmit(submitButton);
      } else {
        this.element.submit();
      }
    } catch (_error) {
      this.showValidationBlock("Unable to check the classroom schedule. Please try again.");
    } finally {
      this.conflictCheckInProgress = false;
      if (this.element.querySelector('input[name="confirm_schedule_conflicts"]')?.value !== "1") {
        submitButton.disabled = false;
      }
    }
  }

  confirmScheduleConflicts(conflicts) {
    if (conflicts.length === 0) return true;

    const details = conflicts
      .map(conflict => `- ${conflict.name} (${conflict.start_time})`)
      .join("\n");

    return window.confirm(
      `Schedule conflicts found with existing classroom programs:\n\n${details}\n\nDo you want to continue creating these programs?`
    );
  }

  classroomProgramCreationCount() {
    const primaryDate = this.hasDateSelectorTarget ? this.dateSelectorTarget.value : "";
    const primaryTime = this.hasTimeSelectorTarget ? this.timeSelectorTarget.value : "";
    if (!primaryDate || !primaryTime) return 1;

    const repeatTimes = Array.from(
      this.element.querySelectorAll('input[name$="[repeat_start_times][]"]')
    )
      .map(input => input.value.trim())
      .filter(Boolean);
    const times = [...new Set([primaryTime, ...repeatTimes])];
    const selectedRepeatMode = this.element.querySelector('input[name$="[repeat_mode]"]:checked')?.value;
    const repeatDates = this.repeatDatesForConfirmation(primaryDate, selectedRepeatMode);

    return new Set(
      repeatDates.flatMap(date => times.map(time => `${date}T${time}`))
    ).size;
  }

  repeatDatesForConfirmation(primaryDate, repeatMode) {
    if (repeatMode === "specific_days") {
      const input = this.element.querySelector('#driving_school_classroom_program_repeat_start_dates');
      let selectedDates = [];

      try {
        selectedDates = input?.value ? JSON.parse(input.value) : [];
      } catch (_error) {
        return [primaryDate];
      }

      return [...new Set([primaryDate, ...selectedDates])];
    }

    if (repeatMode !== "custom") return [primaryDate];

    const intervalCount = Number(
      this.element.querySelector('#driving_school_classroom_program_repeat_every_count')?.value
    );
    const intervalUnit = this.element.querySelector('#driving_school_classroom_program_repeat_every_unit')?.value;
    const occurrences = Number(
      this.element.querySelector('#driving_school_classroom_program_repeat_occurrences')?.value
    );
    const selectedWeekdays = new Set(
      this.dayCheckboxTargets
        .filter(checkbox => checkbox.checked)
        .map(checkbox => ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"].indexOf(checkbox.value.toLowerCase()))
    );

    if (!intervalCount || !intervalUnit || !occurrences || selectedWeekdays.size === 0) {
      return [primaryDate];
    }

    const dates = [primaryDate];
    let candidate = this.dateFromInput(primaryDate);
    let guard = 0;

    while (dates.length - 1 < occurrences && guard < 500) {
      guard += 1;
      candidate = this.advanceRepeatDate(candidate, intervalCount, intervalUnit);

      if (selectedWeekdays.has(candidate.getDay())) {
        dates.push(this.dateToInput(candidate));
      }
    }

    return dates;
  }

  dateFromInput(value) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day, 12);
  }

  dateToInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  advanceRepeatDate(date, count, unit) {
    const nextDate = new Date(date);

    if (unit === "day") {
      nextDate.setDate(nextDate.getDate() + count);
      return nextDate;
    }

    if (unit === "week") {
      nextDate.setDate(nextDate.getDate() + (count * 7));
      return nextDate;
    }

    const targetMonth = nextDate.getMonth() + count;
    const targetYear = nextDate.getFullYear() + Math.floor(targetMonth / 12);
    const normalizedMonth = ((targetMonth % 12) + 12) % 12;
    const lastDayOfTargetMonth = new Date(targetYear, normalizedMonth + 1, 0).getDate();
    nextDate.setFullYear(targetYear, normalizedMonth, Math.min(nextDate.getDate(), lastDayOfTargetMonth));
    return nextDate;
  }

  validateForm(isEdit) {
    let validationErrors = [];

    let fieldsToValidate = isEdit ? [] : [
      { id: "picked_slot_iso_time", name: "Start time", validation: "required|datetime" },
      { id: "driving_school_classroom_program_duration_in_minutes", name: "Duration", validation: "required|positiveNumber" },
      { id: "driving_school_classroom_program_number_of_sessions", name: "Total sessions", validation: "required|positiveNumber" },
      { id: "driving_school_classroom_program_number_of_sessions_per_day", name: "Sessions per day", validation: "required|positiveNumber" },
      { 
        id: "driving_school_classroom_program_gap_between_sessions_in_minutes", 
        name: "Session gap (mins)", 
        hidden: this.gapBetweenSessionsTarget.classList.contains('u-hidden'),
        validation: "required|positiveNumber" 
      },
      { type: "daysSessionsRun" },
      { id: "driving_school_classroom_program_instructor_ids", name: "Instructor", validation: "required" },
      { 
        id: "driving_school_classroom_program_driving_school_office_id", 
        name: "Location", 
        validation: "required",
        hidden: this.locationTypeInPersonTarget.classList.contains('u-hidden')
      },
      {
        id: "driving_school_classroom_program_max_number_of_in_person_attendees", 
        name: "Max attendees", 
        hidden: this.locationTypeInPersonTarget.classList.contains('u-hidden'), 
        validation: "required|positiveNumber" 
      },
      { 
        id: "driving_school_classroom_program_max_number_of_remote_attendees", 
        name: "Max remote attendees", 
        hidden: this.locationTypeRemoteTarget.classList.contains('u-hidden'), 
        validation: "required|positiveNumber" 
      },
      { type: "coursework" }
    ];

    validationErrors.push(...this.validateTeenMinimumAge());
    validationErrors.push(...this.validateDriveTestScheduling());

    fieldsToValidate.forEach(field_data => {
      if (!field_data.hidden) {
        if (field_data.type === "daysSessionsRun") {
          let selectedDays = this.dayCheckboxTargets.filter(checkbox => checkbox.checked).map(checkbox => checkbox.value);
          let daysSessionsRun = selectedDays.map(day => day.toLowerCase()).join(", ");
    
          if (daysSessionsRun === "") {
            validationErrors.push("At least one day must be selected for sessions.");
          }
        } else if (field_data.type === "coursework") {
          validationErrors.push(...this.validateCoursework());
        } else {
          let field = document.getElementById(field_data.id);
          
          if (field) {
            let value = field.value;
            let validationRules = field_data.validation.split('|');
      
            for (let rule of validationRules) {
              switch (rule) {
                case 'required':
                  if (!value) {
                    if (field_data.name === "Instructor") {
                      // validationErrors.push("An instructor must be selected.");
                      // this is now allowed as NOVA need it. 
                    } else {
                      validationErrors.push(`${field_data.name} is required.`);
                    }
                    return;
                  }
                  break;
      
                case 'datetime':
                  if (value && isNaN(Date.parse(value))) {
                    validationErrors.push(`${field_data.name} must be a valid date-time.`);
                    return;
                  }
                  break;
      
                case 'positiveNumber':
                  if (value === "") {
                    validationErrors.push(`${field_data.name} cannot be empty.`);
                    return;
                  } else if (isNaN(value) || Number(value) <= 0) {
                    validationErrors.push(`${field_data.name} must be greater than 0.`);
                    return;
                  }
                  break;
              }
            }
          }
        }
      }
    });
    
    return validationErrors;
  }

  validateTeenMinimumAge() {
    if (!this.hasAvailableToTeensTarget || !this.availableToTeensTarget.checked) return [];
    if (!this.hasTeenMinimumAgeYearsTarget || !this.hasTeenMinimumAgeMonthsTarget) return [];

    const years = this.teenMinimumAgeYearsTarget.value;
    const months = this.teenMinimumAgeMonthsTarget.value;

    if ((years && !months) || (!years && months)) {
      return ["Teen minimum age must include both years and months, or neither."];
    }

    return [];
  }

  validateCoursework() {
    const { errors, total, anyCompletedItem } = this.courseworkSummary();
    if (!anyCompletedItem) return errors;

    const rounded = Math.round(total * 100) / 100;
    if (rounded !== 100) {
      errors.push(`Coursework weights must total 100% (currently ${rounded}%).`);
    }

    return errors;
  }

  validateDriveTestScheduling() {
    if (!this.driveTestSchedulingLimitedByCoursework()) return [];

    const errors = [];
    const { anyCompletedItem } = this.courseworkSummary();

    if (!anyCompletedItem) {
      errors.push("Add at least one coursework item before limiting drive test scheduling by coursework.");
    }

    const threshold = this.hasDriveTestMinimumCourseworkScorePercentTarget
      ? this.driveTestMinimumCourseworkScorePercentTarget.value.trim()
      : "";

    if (!threshold) {
      errors.push("Minimum total score required is required.");
      return errors;
    }

    const numericThreshold = Number(threshold);
    if (Number.isNaN(numericThreshold) || numericThreshold < 0 || numericThreshold > 100) {
      errors.push("Minimum total score required must be between 0 and 100.");
    }

    return errors;
  }

  courseworkSummary() {
    const errors = [];
    const rows = this.element.querySelectorAll(
      '[data-controller="add-to-form"] [data-add-to-form-target="container"] [data-add-to-form-target="row"]'
    );

    let total = 0;
    let anyCompletedItem = false;

    rows.forEach((row, index) => {
      const nameInput = row.querySelector('input[name$="[name]"]');
      const weightInput = row.querySelector('input[name$="[weight]"]');

      if (!nameInput && !weightInput) return;

      const nameValue = (nameInput?.value || "").trim();
      const weightValue = (weightInput?.value || "").trim();

      const namePresent = nameValue.length > 0;
      const weightPresent = weightValue.length > 0;

      if (!namePresent && !weightPresent) return;

      if (namePresent && !weightPresent) {
        errors.push(`Coursework item ${index + 1} must have a weight.`);
      }

      if (weightPresent && !namePresent) {
        errors.push(`Coursework item ${index + 1} must have a name.`);
      }

      const weight = Number(weightValue);
      if (Number.isNaN(weight)) {
        errors.push(`Coursework item ${index + 1} weight must be a number.`);
      }

      anyCompletedItem = true;
      total += weight;
    });

    return { errors, total, anyCompletedItem };
  }

  driveTestSchedulingLimitedByCoursework() {
    const selectedLimitMode = this.element.querySelector('input[name$="[drive_test_scheduling_limit_mode]"]:checked');
    return selectedLimitMode?.value === "coursework";
  }

  showValidationBlock(message) {
    const validationBlock = document.getElementById("validation-block");
    const validationMessage = document.getElementById("validation-message");
  
    validationMessage.innerHTML = message;
    validationBlock.hidden = false;
    validationMessage.hidden = false;
  }
}
